import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ProfileService from "../../services/ProfileService";
import RechargeService from "../../services/RechargeService";
import SideBar from "../../components/Sidebar";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const RechargeOrderSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    type,
    number,
    amount,
    selectedOperator,
    selectedCircle,
    planDescription,
  } = location.state || {};

  const isPrepaid = type === "PREPAID";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balanceData, setBalanceData] = useState();
  const [commercialData, setCommercialData] = useState();

  useEffect(() => {
    fetchBalance();
    getCommercialHandler();
  }, []);

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const result = await ProfileService.fetchBalance();
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        setBalanceData(data);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const getCommercialHandler = async () => {
    setLoading(true);
    try {
      const result = await RechargeService.getCommercial(
        number,
        selectedOperator.code,
        amount,
        false,
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        setCommercialData(data);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  const transactionHandler = async () => {
    if (isSubmitting) return; // prevent double submission
    setIsSubmitting(true);
    setLoading(true);
    try {
      const result = await RechargeService.transaction(
        number,
        selectedOperator.code,
        amount,
        "",
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData?.message == "Insufficient Main Wallet Balance") {
        Swal.fire("Oops!", responseData.message, "error");
        return;
      }

      navigate("/recharge-order-confirmation", {
        state: {
          type,
          number,
          amount,
          selectedOperator,
          selectedCircle,
          planDescription,
          commercialData,
          responseData,
        },
        replace: true,
      });
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      {loading && <LoadingScreen />}

      <div className="row">
        {/* Sidebar */}
        <SideBar />

        {/* Main Content */}
        <div className="col-md-9 mb-5">
          <div
            className="card p-4 shadow-sm"
            style={{ minHeight: "80vh", backgroundColor: "#f8f9fa" }}
          >
            <h4 className="text-center fw-bold mb-4">Recharge Summary</h4>

            <div className="d-flex justify-content-center ">
              <div
                className="card p-4 shadow-sm"
                style={{
                  maxWidth: "530px",
                  width: "100%",
                  borderRadius: "16px",
                  backgroundColor: "#ffffff",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">
                      {selectedOperator?.title || "Operator"}
                    </h5>
                    <small className="text-muted">{type || "N/A"}</small>
                  </div>
                  <div className="d-flex align-items-center">
                    {selectedOperator?.image && (
                      <img
                        src={selectedOperator.image}
                        alt={selectedOperator.title}
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </div>
                </div>

                <ul className="list-group list-group-flush mb-3">
                  {number && (
                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Recharge Number</span>
                      <span className="fw-bold fs-6">{number || "N/A"}</span>
                    </li>
                  )}

                  <li className="list-group-item d-flex justify-content-between">
                    <span className="text-secondary">Recharge Amount</span>
                    <span className="fw-bold fs-6">₹{amount || "0.00"}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span className="text-secondary">Operator</span>
                    <span className="fw-bold fs-6">
                      {selectedOperator?.title || "N/A"}
                    </span>
                  </li>

                  {isPrepaid && (
                    <li className="list-group-item d-flex justify-content-between">
                      <span className="text-secondary">Circle</span>
                      <span className="fw-bold fs-6">
                        {selectedCircle || "N/A"}
                      </span>
                    </li>
                  )}
                </ul>

                {/* Show Plan Description if PREPAID */}
                {isPrepaid && planDescription && (
                  <div className="alert alert-info mb-3">
                    <strong>Plan Description:</strong> {planDescription}
                  </div>
                )}

                {balanceData && (
                  <div className="text-center mb-2">
                    <h6>
                      Available Balance:{" "}
                      <span className="fw-bold text-success">
                        ₹{balanceData?.main_wallet || "0.00"}
                      </span>
                    </h6>
                  </div>
                )}

                {/* Highlighted Payment Section */}
                {commercialData && (
                  <div
                    className="p-3 rounded mb-3"
                    style={{
                      backgroundColor: "#f1f3f5",
                      border: "1px solid #ced4da",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {commercialData.commercial_amount > 0 && (
                      <div
                        className="d-flex align-items-center"
                        style={{
                          backgroundColor: "#e6f4ea",
                          border: "1px solid #a3d3a1",
                          padding: "0.5rem 1rem",
                          borderRadius: "8px",
                          color: "#2f7a32",
                          fontWeight: "600",
                          fontSize: "0.95rem",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-tag-fill me-2"
                          viewBox="0 0 16 16"
                          style={{ marginRight: "0.5rem" }}
                        >
                          <path d="M2 2a1 1 0 0 0-1 1v3.586a1 1 0 0 0 .293.707l6 6a1 1 0 0 0 1.414 0l3.586-3.586a1 1 0 0 0 0-1.414l-6-6A1 1 0 0 0 5.586 2H2z" />
                          <path d="M5.5 5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z" />
                        </svg>
                        Discount: ₹{commercialData.commercial_amount}
                      </div>
                    )}

                    <div
                      className="fw-bold text-primary"
                      style={{ minWidth: "150px", textAlign: "right" }}
                    >
                      <span style={{ fontSize: "16px" }}>Payable Amount:</span>{" "}
                      <span style={{ fontSize: "17px" }}>
                        ₹{commercialData.payable_amount || amount || "0.00"}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-primary w-100 py-2 fs-6"
                  onClick={transactionHandler}
                >
                  {isSubmitting ? "Processing..." : "Make Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RechargeOrderSummary;
